import React, { useCallback, useEffect, useMemo, useState, } from 'react';
import classnames from 'classnames';
import ModalProvider from 'choerodon-ui/pro/lib/modal-provider';
import { TabsPosition, TabsType } from './enum';
import { getDataAttr, getDefaultActiveKey, getDefaultActiveKeyInGroup, getDefaultGroupKey, isVertical, normalizePanes } from './utils';
import { Size } from '../_util/enum';
import { getConfig, getPrefixCls } from '../configure';
import warning from '../_util/warning';
import TabBar from './TabBar';
import Icon from '../icon';
import TabContent from './TabContent';
import isFlexSupported from '../_util/isFlexSupported';
import TabsContext from './TabsContext';
function isAnimated(animated) {
    return typeof animated === 'object';
}
const TabsWithContext = function TabsWithContext(props) {
    const { tabPosition, className, destroyInactiveTabPane, style, size, type, tabBarStyle, inkBarStyle = getConfig('tabsInkBarStyle'), hideAdd, animated = true, tabBarGutter, onEdit, tabBarExtraContent, hideOnlyGroup, customizedCode, customizable, children, defaultActiveKey: propDefaultActiveKey, setCustomized, customized, prefixCls: customizePrefixCls, activeKey: propActiveKey, onChange, onTabClick, onPrevClick, onNextClick, keyboard, ...restProps } = props;
    const hasPropActiveKey = 'activeKey' in props;
    const prefixCls = getPrefixCls('tabs', customizePrefixCls);
    const saveCustomized = useCallback((newCustomized) => {
        if (customizable) {
            setCustomized(newCustomized);
            if (customizedCode) {
                const customizedSave = getConfig('customizedSave');
                customizedSave(customizedCode, newCustomized, 'Tabs');
            }
        }
    }, [customizable, customizedCode]);
    const [totalPanelsMap, groupedPanelsMap] = useMemo(() => normalizePanes(children, customized), [children, customized]);
    const defaultActiveKey = useMemo(() => {
        const option = {
            activeKey: propActiveKey,
            defaultActiveKey: propDefaultActiveKey,
        };
        return getDefaultActiveKey(totalPanelsMap, groupedPanelsMap, option);
    }, []);
    const actuallyDefaultActiveKey = useMemo(() => {
        if (customized) {
            const $defaultActiveKey = customized.defaultActiveKey;
            if ($defaultActiveKey !== undefined) {
                if (onChange && $defaultActiveKey !== defaultActiveKey) {
                    onChange($defaultActiveKey);
                }
                return $defaultActiveKey;
            }
        }
        return defaultActiveKey;
    }, [defaultActiveKey]);
    const [activeKey, setActiveKey] = useState(actuallyDefaultActiveKey);
    const activeGroupKey = useMemo(() => {
        if (groupedPanelsMap.size) {
            if (activeKey) {
                for (const [groupKey, { panelsMap }] of groupedPanelsMap) {
                    for (const [panelKey] of panelsMap) {
                        if (panelKey === activeKey) {
                            return groupKey;
                        }
                    }
                }
            }
            return getDefaultGroupKey(groupedPanelsMap);
        }
    }, [groupedPanelsMap, activeKey]);
    const currentGroup = activeGroupKey && groupedPanelsMap.get(activeGroupKey);
    const currentPanelMap = currentGroup ? currentGroup.panelsMap : totalPanelsMap;
    const changeActiveKey = useCallback((key, byGroup) => {
        if (activeKey !== key) {
            if (!byGroup && currentGroup) {
                currentGroup.lastActiveKey = key;
            }
            if (!hasPropActiveKey) {
                setActiveKey(key);
            }
            if (onChange) {
                onChange(key);
            }
        }
    }, [hasPropActiveKey, activeKey, onChange, currentGroup]);
    const value = {
        defaultActiveKey: propDefaultActiveKey,
        actuallyDefaultActiveKey,
        propActiveKey,
        prefixCls,
        keyboard,
        tabBarPosition: tabPosition,
        hideOnlyGroup,
        customizable,
        customized,
        saveCustomized,
        activeKey,
        activeGroupKey,
        changeActiveKey,
        groupedPanelsMap,
        currentPanelMap,
        totalPanelsMap,
        onTabClick,
        onPrevClick,
        onNextClick,
        children,
    };
    const inkBarAnimated = isAnimated(animated) ? animated.inkBar : animated;
    let tabPaneAnimated = isAnimated(animated) ? animated.tabPane : animated;
    // card tabs should not have animation
    if (type !== TabsType.line) {
        tabPaneAnimated = 'animated' in props ? tabPaneAnimated : false;
    }
    const isCard = type === TabsType.card || type === TabsType['editable-card'];
    warning(!(isCard && (size === Size.small || size === Size.large)), 'Tabs[type=card|editable-card] doesn\'t have small or large size, it\'s by designed.');
    const createNewTab = useCallback((e) => {
        if (onEdit) {
            onEdit(e, 'add');
        }
    }, [onEdit]);
    const removeTab = useCallback((targetKey, e) => {
        e.stopPropagation();
        if (!targetKey) {
            return;
        }
        if (onEdit) {
            onEdit(targetKey, 'remove');
        }
    }, [onEdit]);
    useEffect(() => {
        if (hasPropActiveKey) {
            if (propActiveKey !== activeKey) {
                setActiveKey(propActiveKey);
            }
        }
        else if (activeKey === undefined || !totalPanelsMap.has(activeKey)) {
            setActiveKey(getDefaultActiveKeyInGroup(currentPanelMap));
        }
    }, [hasPropActiveKey, propActiveKey, activeKey, totalPanelsMap, currentPanelMap]);
    const cls = classnames(prefixCls, `${prefixCls}-${tabPosition}`, `${prefixCls}-${type}`, isVertical(tabPosition) ? `${prefixCls}-vertical` : `${prefixCls}-horizontal`, {
        [`${prefixCls}-${size}`]: !!size,
        [`${prefixCls}-card`]: isCard,
        [`${prefixCls}-no-animation`]: !tabPaneAnimated,
        'no-flex': !isFlexSupported(),
    }, className);
    const extraContent = !hideAdd && type === TabsType['editable-card'] ? (
    // Add new tab handler
    React.createElement("span", { key: "tabBarExtraContent" },
        React.createElement(Icon, { type: "add", className: `${prefixCls}-new-tab`, onClick: createNewTab }),
        tabBarExtraContent)) : tabBarExtraContent;
    const tabBarProps = {
        inkBarAnimated,
        extraContent,
        style: tabBarStyle,
        tabBarGutter,
        type,
        onRemoveTab: removeTab,
        inkBarStyle,
    };
    const tabContentProps = {
        animatedWithMargin: true,
        animated: tabPaneAnimated,
        destroyInactiveTabPane,
    };
    const contents = [
        React.createElement(TabBar, Object.assign({ key: "tabBar" }, tabBarProps)),
        React.createElement(TabContent, Object.assign({ key: "tabContent" }, tabContentProps)),
    ];
    if (tabPosition === TabsPosition.bottom) {
        contents.reverse();
    }
    const tabs = (React.createElement("div", Object.assign({ className: cls, style: style }, getDataAttr(restProps)), contents));
    return (React.createElement(TabsContext.Provider, { value: value }, customizable ? (React.createElement(ModalProvider, null, tabs)) : tabs));
};
TabsWithContext.displayName = 'TabsWithContext';
export default TabsWithContext;
